/**
 * Gráfico de projeção de saldo e fluxos (receitas/despesas).
 * Usado no dashboard financeiro.
 */

import React, { useState, useMemo, useEffect, useRef, useId } from 'react'
import { ChevronDown } from 'lucide-react'
import { useAuth } from '../../../../contexts/AuthContext'
import DailySalesProjectionPanel, {
  formatSalesProjectionCurrency,
  parseSalesProjectionAmount
} from '../DailySalesProjectionPanel/DailySalesProjectionPanel'
import PendingPurchasesProjectionPanel from '../PendingPurchasesProjectionPanel/PendingPurchasesProjectionPanel'
import { aggregatePurchaseOutflows } from '../../../../utils/purchaseProjectionUtils'
import { useBalanceProjectionChart } from './useBalanceProjectionChart'
import './BalanceProjectionChart.css'

const PROJECTION_DAY_OPTIONS = [
  { value: 2, label: '2 dias (48h)' },
  { value: 7, label: '7 dias' },
  { value: 30, label: '30 dias' },
  { value: 60, label: '60 dias' },
  { value: 90, label: '90 dias' },
  { value: 120, label: '120 dias' },
  { value: 150, label: '150 dias' },
  { value: 180, label: '180 dias' }
]

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0)
}

function inflowProjectionKey(user) {
  const schema = user?.organization_schema || 'public'
  return `wfmed_finance_inflow_projection:${schema}:${user?.id || 'anon'}`
}

function readSavedInflow(user) {
  try {
    const raw = localStorage.getItem(inflowProjectionKey(user))
    if (raw == null || raw === '') return '0'
    const parsed = parseSalesProjectionAmount(raw)
    return Number.isFinite(parsed) ? String(parsed) : '0'
  } catch (error) {
    return '0'
  }
}

function BalanceProjectionChart({
  balanceProjection: balanceProjectionData,
  projectionDays,
  onProjectionDaysChange
}) {
  const chartId = useId().replace(/:/g, '')
  const { user } = useAuth()
  const [hoveredPoint, setHoveredPoint] = useState(null)
  const [dailySalesAmount, setDailySalesAmount] = useState('0')
  const [selectedPurchaseIds, setSelectedPurchaseIds] = useState([])
  const [purchaseRequisitions, setPurchaseRequisitions] = useState([])
  const [projectionPeriodOpen, setProjectionPeriodOpen] = useState(false)
  const projectionPeriodRef = useRef(null)

  useEffect(() => {
    setDailySalesAmount(readSavedInflow(user))
  }, [user])

  const saveDailySalesAmount = (value) => {
    const next = String(parseSalesProjectionAmount(value))
    setDailySalesAmount(next)
    try {
      localStorage.setItem(inflowProjectionKey(user), next)
    } catch (error) {
      console.warn('Não foi possível salvar a projeção de entradas', error)
    }
  }

  useEffect(() => {
    if (!projectionPeriodOpen) return undefined

    const handleClickOutside = (event) => {
      if (projectionPeriodRef.current && !projectionPeriodRef.current.contains(event.target)) {
        setProjectionPeriodOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [projectionPeriodOpen])

  const selectedPurchases = useMemo(
    () => purchaseRequisitions.filter((r) => selectedPurchaseIds.includes(r.id)),
    [purchaseRequisitions, selectedPurchaseIds]
  )

  const purchaseOutflowEvents = useMemo(
    () => aggregatePurchaseOutflows(selectedPurchases),
    [selectedPurchases]
  )

  const dailySalesAmountNum = parseSalesProjectionAmount(dailySalesAmount)
  const dailySalesProjectionActive = dailySalesAmountNum > 0

  const { balanceProjection, inflowPath, outflowPath, balancePath } = useBalanceProjectionChart({
    balanceProjectionData,
    dailySalesProjectionActive,
    dailySalesAmount,
    purchaseOutflowEvents
  })

  const glowFilterId = `balance-projection-glow-${chartId}`
  const balanceGradId = `balance-projection-grad-${chartId}`

  return (
    <div className="balance-projection-chart cash-flow-analysis-balance-chart">
      <div className="cash-flow-analysis-balance-chart-container balance-projection-chart-wrap">
        <div className="cash-flow-analysis-balance-chart-header">
          <div>
            <h3 className="cash-flow-analysis-balance-chart-title">
              Projeção de Saldo & Fluxos
            </h3>
            <p className="cash-flow-analysis-balance-chart-subtitle">
              Impacto das Receitas e Despesas no Saldo Final
            </p>
          </div>
          <div className="balance-projection-chart-header-actions">
            <div className="balance-projection-chart-fab-group">
              <DailySalesProjectionPanel
                dailyAmount={dailySalesAmount}
                onDailyAmountChange={saveDailySalesAmount}
              />
              <PendingPurchasesProjectionPanel
                selectedIds={selectedPurchaseIds}
                onSelectionChange={setSelectedPurchaseIds}
                onRequisitionsLoaded={setPurchaseRequisitions}
              />
            </div>
            <div className="cash-flow-analysis-balance-chart-legend">
              <div className="cash-flow-analysis-balance-chart-legend-item">
                <div className="cash-flow-analysis-balance-chart-legend-color balance" />
                <span>Saldo Acumulado</span>
              </div>
              <div className="cash-flow-analysis-balance-chart-legend-item">
                <div className="cash-flow-analysis-balance-chart-legend-color inflow" />
                <span>Receitas</span>
              </div>
              <div className="cash-flow-analysis-balance-chart-legend-item">
                <div className="cash-flow-analysis-balance-chart-legend-color outflow" />
                <span>Despesas</span>
              </div>
              {balanceProjection.hasSalesProjection && (
                <div className="cash-flow-analysis-balance-chart-legend-item">
                  <div className="cash-flow-analysis-balance-chart-legend-color sales-projection" />
                  <span>Entrada projetada</span>
                </div>
              )}
              {balanceProjection.hasPurchaseProjection && (
                <div className="cash-flow-analysis-balance-chart-legend-item">
                  <div className="cash-flow-analysis-balance-chart-legend-color purchase-projection" />
                  <span>Compras simuladas</span>
                </div>
              )}
            </div>
            <div className="balance-projection-chart-period" ref={projectionPeriodRef}>
              <span className="balance-projection-chart-period-label">Período:</span>
              <div className="balance-projection-chart-period-dropdown">
                <button
                  type="button"
                  className="balance-projection-chart-period-trigger"
                  onClick={() => setProjectionPeriodOpen((open) => !open)}
                  aria-expanded={projectionPeriodOpen}
                  aria-haspopup="listbox"
                >
                  {PROJECTION_DAY_OPTIONS.find((o) => o.value === projectionDays)?.label ?? `${projectionDays} dias`}
                  <ChevronDown
                    size={14}
                    className={`balance-projection-chart-period-chevron${projectionPeriodOpen ? ' is-open' : ''}`}
                    aria-hidden
                  />
                </button>
                {projectionPeriodOpen && (
                  <ul className="balance-projection-chart-period-menu" role="listbox">
                    {PROJECTION_DAY_OPTIONS.map((opt) => (
                      <li key={opt.value}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={projectionDays === opt.value}
                          className={`balance-projection-chart-period-option${projectionDays === opt.value ? ' is-selected' : ''}`}
                          onClick={() => {
                            onProjectionDaysChange(opt.value)
                            setProjectionPeriodOpen(false)
                          }}
                        >
                          {opt.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="cash-flow-analysis-balance-chart-content">
          {dailySalesProjectionActive && (
            <div className="balance-projection-daily-sales-badge" aria-hidden>
              {formatSalesProjectionCurrency(dailySalesAmountNum)}/dia
            </div>
          )}
          {balanceProjection.chartData.length > 0 ? (
            <>
              <div className="cash-flow-analysis-balance-chart-grid" />
              <svg
                viewBox="0 0 1000 300"
                className="cash-flow-analysis-balance-chart-svg"
                preserveAspectRatio="none"
              >
                <defs>
                  <filter id={glowFilterId}>
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                  <linearGradient id={balanceGradId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22c55e" stopOpacity="0.08" />
                    <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  d={`${balancePath} L 1000,300 L 0,300 Z`}
                  fill={`url(#${balanceGradId})`}
                  className="cash-flow-analysis-balance-chart-area"
                />
                <path
                  d={inflowPath}
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="0.8"
                  strokeOpacity="0.4"
                  strokeDasharray="4 2"
                />
                <path
                  d={outflowPath}
                  fill="none"
                  stroke="#f43f5e"
                  strokeWidth="0.8"
                  strokeOpacity="0.4"
                  strokeDasharray="4 2"
                />
                <path
                  d={balancePath}
                  fill="none"
                  stroke="#16a34a"
                  strokeWidth="2"
                  strokeLinecap="round"
                  filter={`url(#${glowFilterId})`}
                  className="cash-flow-analysis-balance-chart-line"
                />
                {balanceProjection.chartData.map((d, i) => (
                  <rect
                    key={i}
                    x={d.x - 16}
                    y="0"
                    width="32"
                    height="300"
                    fill="transparent"
                    className="cash-flow-analysis-balance-chart-hitbox"
                    onMouseEnter={() => setHoveredPoint(i)}
                    onMouseLeave={() => setHoveredPoint(null)}
                  />
                ))}
                {hoveredPoint !== null && balanceProjection.chartData[hoveredPoint] && (
                  <g className="cash-flow-analysis-balance-chart-hover">
                    <line
                      x1={balanceProjection.chartData[hoveredPoint].x}
                      y1="0"
                      x2={balanceProjection.chartData[hoveredPoint].x}
                      y2="300"
                      stroke="#22c55e"
                      strokeWidth="1"
                      strokeDasharray="6 4"
                    />
                    <circle
                      cx={balanceProjection.chartData[hoveredPoint].x}
                      cy={balanceProjection.chartData[hoveredPoint].yBalance}
                      r="6"
                      fill="#16a34a"
                      stroke="white"
                      strokeWidth="2"
                    />
                  </g>
                )}
              </svg>
              {hoveredPoint !== null && balanceProjection.chartData[hoveredPoint] && (
                <div
                  className="cash-flow-analysis-balance-chart-tooltip"
                  style={{
                    left: `${(balanceProjection.chartData[hoveredPoint].x / 1000) * 100}%`,
                    transform: hoveredPoint > 15 ? 'translateX(-110%)' : 'translateX(10%)'
                  }}
                >
                  <div className="cash-flow-analysis-balance-chart-tooltip-header">
                    <span className="cash-flow-analysis-balance-chart-tooltip-label">
                      Previsão Diária
                    </span>
                    <span className="cash-flow-analysis-balance-chart-tooltip-date">
                      {balanceProjection.chartData[hoveredPoint].label}
                    </span>
                  </div>
                  <div className="cash-flow-analysis-balance-chart-tooltip-content">
                    <div className="cash-flow-analysis-balance-chart-tooltip-item">
                      <p className="cash-flow-analysis-balance-chart-tooltip-item-label">
                        Saldo Final do Dia
                      </p>
                      <p className="cash-flow-analysis-balance-chart-tooltip-item-value">
                        {formatCurrency(balanceProjection.chartData[hoveredPoint].balance)}
                      </p>
                    </div>
                    <div className="cash-flow-analysis-balance-chart-tooltip-details">
                      <div>
                        <p className="cash-flow-analysis-balance-chart-tooltip-detail-label inflow">
                          Receita
                        </p>
                        <p className="cash-flow-analysis-balance-chart-tooltip-detail-value">
                          +{formatCurrency(
                            balanceProjection.chartData[hoveredPoint].baseInflow
                            ?? balanceProjection.chartData[hoveredPoint].inflow
                          )}
                        </p>
                      </div>
                      {balanceProjection.chartData[hoveredPoint].projectedSales > 0 && (
                        <div>
                          <p className="cash-flow-analysis-balance-chart-tooltip-detail-label sales">
                            Entrada projetada
                          </p>
                          <p className="cash-flow-analysis-balance-chart-tooltip-detail-value">
                            +{formatCurrency(balanceProjection.chartData[hoveredPoint].projectedSales)}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className="cash-flow-analysis-balance-chart-tooltip-detail-label outflow">
                          Despesa
                        </p>
                        <p className="cash-flow-analysis-balance-chart-tooltip-detail-value">
                          -{formatCurrency(
                            balanceProjection.chartData[hoveredPoint].baseOutflow
                            ?? balanceProjection.chartData[hoveredPoint].outflow
                          )}
                        </p>
                      </div>
                      {balanceProjection.chartData[hoveredPoint].purchaseOutflow > 0 && (
                        <div>
                          <p className="cash-flow-analysis-balance-chart-tooltip-detail-label purchase">
                            Compra simulada
                          </p>
                          <p className="cash-flow-analysis-balance-chart-tooltip-detail-value">
                            -{formatCurrency(balanceProjection.chartData[hoveredPoint].purchaseOutflow)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="cash-flow-analysis-balance-chart-labels">
                {balanceProjection.chartData.filter((_, i) => i % 5 === 0).map((d) => (
                  <span key={d.day ?? d.date ?? d.label} className="cash-flow-analysis-balance-chart-label">
                    {d.label}
                  </span>
                ))}
              </div>
            </>
          ) : (
            <div className="cash-flow-analysis-balance-chart-empty">
              <p>Nenhum dado disponível</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BalanceProjectionChart
